import classnames from 'classnames'

export interface ExpandableMobileNavAction {
  icon: React.ReactNode
  id: string
  label: string
  onClick?: () => void
}

export interface ExpandableMobileNavProps {
  ariaLabel?: string
  centerButtonClassName?: string
  centerContent?: React.ReactNode
  centerLabel?: string
  className?: string
  collapseLabel?: string
  expandLabel?: string
  isCompact?: boolean
  isExpanded: boolean
  leftActions: ExpandableMobileNavAction[]
  onToggle: () => void
  rightActions: ExpandableMobileNavAction[]
}

export default function ExpandableMobileNav({
  ariaLabel = 'Mobile actions',
  centerButtonClassName,
  centerContent,
  centerLabel,
  className,
  collapseLabel = 'Collapse navigation',
  expandLabel = 'Expand navigation',
  isCompact = false,
  isExpanded,
  leftActions,
  onToggle,
  rightActions
}: ExpandableMobileNavProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className={classnames(
        'fixed inset-x-0 bottom-0 z-40 mx-auto flex w-full justify-center px-4 pb-4 md:hidden',
        className
      )}
    >
      <div
        className={classnames(
          'relative flex h-16 items-center justify-center overflow-hidden border border-zinc-200/80 bg-white/90 shadow-[0_18px_40px_rgba(15,23,42,0.16)] backdrop-blur transition-[width,border-radius,padding] duration-300',
          isCompact
            ? 'w-16 rounded-full px-1 py-1'
            : 'w-full max-w-sm rounded-[2rem] px-3 py-2'
        )}
      >
        {leftActions.map((action, index) => (
          <button
            key={action.id}
            type="button"
            aria-label={action.label}
            onClick={action.onClick}
            className={classnames(
              'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 text-zinc-700 transition duration-300',
              index === 0 ? 'left-3' : 'left-[4.75rem]',
              isExpanded
                ? 'translate-x-0 opacity-100'
                : 'pointer-events-none -translate-x-3 opacity-0'
            )}
          >
            {action.icon}
          </button>
        ))}

        <button
          type="button"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? collapseLabel : expandLabel}
          onClick={onToggle}
          className={classnames(
            'relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-zinc-950 text-sm font-semibold tracking-[0.18em] text-white shadow-lg transition hover:bg-zinc-800',
            centerButtonClassName
          )}
        >
          {centerContent ?? centerLabel}
        </button>

        {rightActions.map((action, index) => (
          <button
            key={action.id}
            type="button"
            aria-label={action.label}
            onClick={action.onClick}
            className={classnames(
              'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 text-zinc-700 transition duration-300',
              index === 0 ? 'right-[4.75rem]' : 'right-3',
              isExpanded
                ? 'translate-x-0 opacity-100'
                : 'pointer-events-none translate-x-3 opacity-0'
            )}
          >
            {action.icon}
          </button>
        ))}
      </div>
    </nav>
  )
}
